// AnnualDate.js

const THIRTY_DAY_MONTHS = new Set([4, 6, 9, 11]);

const toPositiveInteger = (value) => {
  if (value === '' || value == null) return null;
  const numeric = Number(value);
  if (!Number.isInteger(numeric) || numeric <= 0) return null;
  return numeric;
};

const normalizeAnnualYear = (value) => {
  const year = toPositiveInteger(value);
  if (!year) return null;
  if (year < 1000 || year > 9999) return null;
  return year;
};

const isLeapYear = (year) => {
  if (!Number.isInteger(year)) return false;
  if (year % 400 === 0) return true;
  if (year % 100 === 0) return false;
  return year % 4 === 0;
};

const getDaysInMonthForYear = (month, year) => {
  if (month === 2) return isLeapYear(year) ? 29 : 28;
  if (THIRTY_DAY_MONTHS.has(month)) return 30;
  return 31;
};

const startOfDay = (value) => {
  const date = value instanceof Date ? new Date(value) : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  return date;
};

export const ANNUAL_DATE_FIELD_TYPE = 'annualdate';

export const ANNUAL_DATE_MONTH_OPTIONS = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

export const isAnnualDateFieldType = (fieldType) => (
  String(fieldType || '').toLowerCase() === ANNUAL_DATE_FIELD_TYPE
);

export const getAnnualDateOptionDayCount = (monthValue) => {
  const month = toPositiveInteger(monthValue);
  if (!month || month > 12) return 31;
  if (month === 2) return 29;
  if (THIRTY_DAY_MONTHS.has(month)) return 30;
  return 31;
};

export const normalizeAnnualDateValue = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;

  const month = toPositiveInteger(value.month);
  const day = toPositiveInteger(value.day);
  const year = normalizeAnnualYear(value.year);

  if (!month || month > 12) return null;
  if (!day || day > getAnnualDateOptionDayCount(month)) return null;

  return {
    month,
    day,
    ...(year ? { year } : {}),
  };
};

export const buildAnnualDateValue = ({ month, day, year }) => {
  if ((month === '' || month == null) && (day === '' || day == null) && (year === '' || year == null)) {
    return null;
  }

  return normalizeAnnualDateValue({ month, day, year });
};

export const getAnnualDateOccurrenceForYear = (value, targetYear) => {
  const normalized = normalizeAnnualDateValue(value);
  const year = toPositiveInteger(targetYear);
  if (!normalized || !year) return null;

  const resolvedDay = Math.min(normalized.day, getDaysInMonthForYear(normalized.month, year));
  return new Date(year, normalized.month - 1, resolvedDay);
};

export const getNextAnnualDateOccurrence = (value, referenceDate = new Date()) => {
  const referenceDay = startOfDay(referenceDate);
  const normalized = normalizeAnnualDateValue(value);
  if (!referenceDay || !normalized) return null;

  const currentYearOccurrence = getAnnualDateOccurrenceForYear(normalized, referenceDay.getFullYear());
  if (currentYearOccurrence && currentYearOccurrence >= referenceDay) {
    return currentYearOccurrence;
  }

  return getAnnualDateOccurrenceForYear(normalized, referenceDay.getFullYear() + 1);
};

export const getPreviousAnnualDateOccurrence = (value, referenceDate = new Date()) => {
  const referenceDay = startOfDay(referenceDate);
  const normalized = normalizeAnnualDateValue(value);
  if (!referenceDay || !normalized) return null;

  const currentYearOccurrence = getAnnualDateOccurrenceForYear(normalized, referenceDay.getFullYear());
  if (currentYearOccurrence && currentYearOccurrence <= referenceDay) {
    return currentYearOccurrence;
  }

  return getAnnualDateOccurrenceForYear(normalized, referenceDay.getFullYear() - 1);
};

export const getAnnualDateOccurrencesInRange = (value, rangeStart, rangeEnd) => {
  const normalized = normalizeAnnualDateValue(value);
  const start = startOfDay(rangeStart);
  const end = startOfDay(rangeEnd);
  if (!normalized || !start || !end || start > end) return [];

  const occurrences = [];
  for (let year = start.getFullYear(); year <= end.getFullYear(); year += 1) {
    const occurrence = getAnnualDateOccurrenceForYear(normalized, year);
    if (!occurrence) continue;
    if (occurrence < start || occurrence > end) continue;
    occurrences.push(occurrence);
  }

  return occurrences;
};

export const getAnnualDateElapsedYears = (value, occurrenceDate = new Date()) => {
  const normalized = normalizeAnnualDateValue(value);
  const occurrence = startOfDay(occurrenceDate);
  if (!normalized?.year || !occurrence) return null;

  const elapsedYears = occurrence.getFullYear() - normalized.year;
  return elapsedYears >= 0 ? elapsedYears : null;
};

export const formatAnnualDateElapsedLabel = (value, occurrenceDate = new Date()) => {
  const elapsedYears = getAnnualDateElapsedYears(value, occurrenceDate);
  if (elapsedYears == null) return '';
  return `${elapsedYears} year${elapsedYears === 1 ? '' : 's'}`;
};

export const formatAnnualDateValue = (value, { locale = 'en-US', includeYear = false } = {}) => {
  const normalized = normalizeAnnualDateValue(value);
  if (!normalized) return '';

  const displayYear = includeYear && normalized.year ? normalized.year : 2000;
  const safeDay = Math.min(normalized.day, getDaysInMonthForYear(normalized.month, displayYear));
  const date = new Date(displayYear, normalized.month - 1, safeDay);
  const options = {
    day: 'numeric',
    month: 'short',
    ...(includeYear && normalized.year ? { year: 'numeric' } : {}),
  };

  return date.toLocaleDateString(locale, options);
};